import { ConditionContextSourceEnum, CURRENT_COMPANY_MODULE_CODE } from '../../../utils/condition-context.util';
import { inject, Injectable } from '@angular/core';
import type { Subscription } from 'rxjs';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ModulesStore } from '../../../../features/admin/modules/modules.store';
import { FormService } from '../../../../features/admin/modules/features/form-builder/form.service';
import type { Module, OptionsItem } from '../../../../features/admin/modules/modules.model';
import { INPUT_OPTIONS } from '../field-config';
import {
  BASED_ON_SELECTED_FIELD,
  createFieldWithOverrides,
  OPERATOR_SELECT_FIELD,
} from '../../../utils/formly-field.util';
import { DynamicOptionsService } from './dynamic-options.service';
import { FormHelper } from '../../../../features/admin/modules/features/form-builder/form.util';
import type { FieldOption } from '../../../../features/admin/modules/features/form-builder/form.model';
import { UserRolesStore } from '../../../../features/admin/user-roles/user-roles.store';
import { CONDITION_DATE_OFFSET_FIELDS, DATE_FIELD_TYPES, UNIT_LABELS } from '../fieldGroup.config';

/** The viewer's company as a condition source (form-builder field conditions only). */
const CURRENT_COMPANY_OPTION = {
  value: ConditionContextSourceEnum.CurrentCompany,
  label: 'Bedrijf van ingelogde gebruiker',
  type: 'module',
};

/** What `getCompareField` answers for that source: a link to the companies module. */
const CURRENT_COMPANY_CONDITION_FIELD: FormlyFieldConfig = {
  key: ConditionContextSourceEnum.CurrentCompany,
  type: 'autocomplete',
  props: {
    label: CURRENT_COMPANY_OPTION.label,
    keyPath: ConditionContextSourceEnum.CurrentCompany,
    optionsArray: [{ optionsType: 'module', moduleCode: CURRENT_COMPANY_MODULE_CODE }],
  },
};

@Injectable({
  providedIn: 'root',
})
export class ConditionFieldsService {
  private modulesStore = inject(ModulesStore);
  private userRolesStore = inject(UserRolesStore);
  private dynamicOptionsService = inject(DynamicOptionsService);
  private formService = inject(FormService);

  /**
   * Gets the effective module code for condition fields.
   * Searches for `conditionsTargetModuleCode` at each level of the form hierarchy.
   * Falls back to the default module code if not found.
   */
  getEffectiveModuleCode(field: FormlyFieldConfig, defaultModuleCode: string): string {
    // Traverse up the parent chain and check for conditionsTargetModuleCode at each level
    let current = field;
    while (current) {
      const form = current.form;
      if (form) {
        // Check if this form level has conditionsTargetModuleCode
        const targetModuleCode = form.get('conditionsTargetModuleCode')?.value;
        if (targetModuleCode) {
          return targetModuleCode;
        }
      }
      current = current.parent;
    }

    return defaultModuleCode;
  }

  // Shared methods for handling field options and comparisons
  getCompareField(field: FormlyFieldConfig, moduleCode: string) {
    const compareFieldValue = this.formService.findFieldValue(field, 'compareField');

    if (compareFieldValue === undefined) {
      return undefined;
    }
    // The viewer's company is no field of this module; answer as a link to the companies module,
    // so the Subveld select lists that module's fields like it does for any company link.
    if (compareFieldValue === ConditionContextSourceEnum.CurrentCompany) {
      return CURRENT_COMPANY_CONDITION_FIELD;
    }

    const selectedModule = this.modulesStore.modules().find(module => module.code === moduleCode);
    return FormHelper.getFieldByKeyPath(compareFieldValue, this.modulesStore.generatedForms()[selectedModule.id]);
  }

  getCompareSubField(field: FormlyFieldConfig, moduleCode: string) {
    const compareFieldValue = this.formService.findFieldValue(field, 'compareSubField');

    if (compareFieldValue === undefined || compareFieldValue === '') {
      return undefined;
    }

    const selectedModule = this.modulesStore.modules().find(module => module.code === moduleCode);
    return FormHelper.getFieldByKeyPath(compareFieldValue, this.modulesStore.generatedForms()[selectedModule.id]);
  }

  hideInputIfFieldHasOptions(field: FormlyFieldConfig, moduleCode: string): boolean {
    const targetField = this.getCompareField(field, moduleCode);
    if (targetField === undefined) {
      return false;
    }

    const optionItem = targetField?.props?.optionsArray?.find(
      (optionItem: OptionsItem) => optionItem.optionsType === 'module'
    );

    if (optionItem) {
      const targetSubField = this.getCompareSubField(field, optionItem.moduleCode);
      if (targetSubField) {
        const inputType = INPUT_OPTIONS.find(type => type.value === targetSubField.type);
        return inputType.options;
      }
    }

    const inputType = INPUT_OPTIONS.find(type => type.value === targetField.type);
    return inputType.options;
  }

  getOptionsOfTargetField(targetField: FormlyFieldConfig) {
    if (targetField === undefined) {
      return [];
    }
    return this.dynamicOptionsService.initializeOptions(targetField);
  }

  getField(field: FormlyFieldConfig, searchField: string, moduleCode: string) {
    const fieldValue = this.formService.findFieldValue(field, searchField);

    if (!fieldValue) {
      return undefined;
    }

    const selectedModule = this.modulesStore.modules().find(module => module.code === moduleCode);
    return FormHelper.getFieldByKeyPath(fieldValue, this.modulesStore.generatedForms()[selectedModule.id]);
  }

  checkMultiSelectArray(field: FormlyFieldConfig) {
    if (!field.model) {
      return field.model;
    }

    if (field.props.multiple && !Array.isArray(field.model.value)) {
      return [field.model.value].filter(Boolean);
    }

    return field.model.value;
  }

  convertToString(field: FormlyFieldConfig) {
    if (!field.model) {
      return field.model;
    }

    if (Array.isArray(field.model.value)) {
      return '';
    }

    return field.model.value;
  }

  /**
   * Options for a subfield select, preserving an unverifiable stored value as a marked extra
   * option — the storedValue semantics (and the `'unknown'` / `'module-unavailable'` markers that
   * `subFieldSelectionHint` explains) live in `FormHelper.moduleLinkedFieldOptions`, shared with
   * the form-builder and mapping-field pickers. `logUnresolvedModuleCode` stays off (the
   * default): here an unresolved module is an expected, silently-handled case, not an error
   * worth logging.
   */
  resolveSubFieldOptions(targetField: FormlyFieldConfig | undefined, storedValue: unknown): FieldOption[] {
    return (
      FormHelper.moduleLinkedFieldOptions(targetField, 'Hele object vergelijken', this.modulesStore, this.formService, {
        storedValue,
      }) ?? []
    );
  }

  /**
   * Memoized options for the `compareSubField` / `valueSubField` selects. Both the
   * `props.options` and `props.description` expressions call this independently — the memo (keyed
   * on the select field, deps compared by value) makes the second call free and removes any
   * dependency on the order in which ngx-formly evaluates a field's expressions.
   */
  subFieldOptions(field: FormlyFieldConfig, defaultModuleCode: string, kind: 'compare' | 'value'): FieldOption[] {
    if (kind === 'compare') {
      const effectiveModuleCode = this.getEffectiveModuleCode(field, defaultModuleCode);
      const storedValue = field.model?.compareSubField;
      return (
        FormHelper.memoizedFieldOptions(
          field,
          [
            kind,
            effectiveModuleCode,
            field.model?.compareField,
            storedValue,
            this.modulesStore.modules(),
            this.modulesStore.generatedForms(),
          ],
          () => this.resolveSubFieldOptions(this.getCompareField(field, effectiveModuleCode), storedValue)
        ) ?? []
      );
    }
    const storedValue = field.model?.valueSubField;
    return (
      FormHelper.memoizedFieldOptions(
        field,
        [
          kind,
          field.model?.fieldKeyPath,
          storedValue,
          this.modulesStore.modules(),
          this.modulesStore.generatedForms(),
        ],
        () => this.resolveSubFieldOptions(this.getFieldKeyPathField(field, defaultModuleCode), storedValue)
      ) ?? []
    );
  }

  /**
   * Hint text for a subfield select. Silent (`undefined`) unless the options carry one of the
   * markers `FormHelper.moduleLinkedFieldOptions` injects for an unverifiable stored value.
   */
  subFieldSelectionHint(options: FieldOption[]): string | undefined {
    return FormHelper.subFieldSelectionHint(options);
  }

  /**
   * Warning text for a stored value the plain "Waarde" input cannot represent.
   *
   * `convertToString` blanks an array, because a text input cannot edit one. Without a note the
   * field reads as empty and the real condition is invisible to whoever opens it.
   */
  storedListValueHint(value: unknown): string | undefined {
    if (!Array.isArray(value) || value.length === 0) {
      return undefined;
    }
    return `Opgeslagen waarde: ${value.join(', ')}. Laat leeg om te behouden.`;
  }

  getFieldKeyPathField(field: FormlyFieldConfig, moduleCode: string) {
    const compareFieldValue = this.formService.findFieldValue(field, 'fieldKeyPath');

    if (compareFieldValue === undefined) {
      return undefined;
    }

    const selectedModule = this.modulesStore.modules().find(module => module.code === moduleCode);

    return FormHelper.getFieldByKeyPath(compareFieldValue, this.modulesStore.generatedForms()[selectedModule.id]);
  }

  /**
   * @param options.allowExternalEntries Opt-in for the `basedOn: 'externalEntries'` option on an
   * `interface` condition set. OFF by default and deliberately so: the option only works on a
   * surface whose runtime calls `ExternalEntriesEvaluatorService.resolve()` first. Everywhere else
   * the counts are never fetched, and — worse — interface-action visibility does not run through
   * `evaluateFiltersAgainstEntry` at all (InterfaceActionDirective reads `compareField` off the
   * entry, which is empty for this shape), so picking it would silently hide the action for good.
   * Pass true only from a surface that resolves the counts.
   */
  /** The fields of the module a condition compares against (the target module when one is set). */
  #compareFieldOptions(field: FormlyFieldConfig, module: Module) {
    // if externalEntries + moduleCode is set

    if (field.model?.moduleCode != null && field.model?.moduleCode != module.code) {
      const selectedModule = this.modulesStore.getModuleByCode(field.model?.moduleCode);
      return this.formService.getFieldsOptionsOfModule(
        this.modulesStore.generatedForms()[selectedModule.id]
      );
    }

    // Check for conditionsTargetModuleCode override (for wizard step conditions)
    const effectiveModuleCode = this.getEffectiveModuleCode(field, module.code);
    const effectiveModule = this.modulesStore.getModuleByCode(effectiveModuleCode);
    if (effectiveModule) {
      return this.formService.getFieldsOptionsOfModule(
        this.modulesStore.generatedForms()[effectiveModule.id]
      );
    }

    return this.formService.getFieldsOptionsOfModule(this.modulesStore.generatedForms()[module.id]);
  }

  getCondtionsField(module: Module, type: 'actions' | 'interface', options?: { allowExternalEntries?: boolean; allowCurrentUser?: boolean; allowCurrentCompany?: boolean }) {
    const basicConditionsField: FormlyFieldConfig = {
      key: 'conditions',
      type: 'repeat',
      props: {
        addText: 'Voorwaarde toevoegen',
        initialCount: 0,
        seperatorText: 'EN',
        label: 'Voorwaarden',
      },
      fieldArray: {
        fieldGroup: [
          {
            props: { label: 'Voorwaarden' },
            fieldGroup: [
              ...(type === 'actions'
                ? [
                    {
                      key: 'uiType',
                      type: 'select',
                      defaultValue: 'currentEntry',
                      props: {
                        label: 'Check uitvoeren op',
                        options: [
                          { value: 'currentEntry', label: 'Huidige entry' },
                          { value: 'externalEntries', label: 'Externe entries' },
                          { value: 'actions', label: 'Linked acties van entry' },
                        ],
                      },
                      expressions: {
                        // Map UI type to backend type
                        'model.type': (field: FormlyFieldConfig) => {
                          if (field.model?.uiType === 'actions') {
                            return 'currentEntry'; // Backend expects currentEntry for linkedActions
                          }
                          return field.model?.uiType || 'currentEntry';
                        },
                      },
                    },
                    {
                      key: 'type',
                      type: 'input',
                      className: 'hidden',
                      props: {
                        label: 'Type (hidden)',
                        disabled: true,
                      },
                    },
                    {
                      key: 'moduleCode',
                      type: 'select',
                      props: {
                        label: 'Module',
                        options: this.modulesStore.modules().map(module => ({
                          value: module.code,
                          label: module.title,
                        })),
                      },
                      expressions: {
                        hide: (field: FormlyFieldConfig) => {
                          return field.model?.uiType !== 'externalEntries';
                        },
                      },
                    },
                    {
                      key: 'actionId',
                      type: 'select',
                      props: {
                        label: 'Selecteer actie',
                        description: 'Kies een actie om te controleren',
                        required: true,
                        options: [],
                      },
                      expressions: {
                        hide: (field: FormlyFieldConfig) => {
                          return field.model?.uiType !== 'actions';
                        },
                        'props.options': () => {
                          // Get all actions from current module
                          const currentModuleActions = this.modulesStore.actions()[module.id] || [];
                          return currentModuleActions.map(action => ({
                            value: action.id,
                            label: action.name || '(Geen naam)',
                          }));
                        },
                      },
                    },
                    {
                      key: 'actionCondition',
                      type: 'select',
                      props: {
                        label: 'Conditie',
                        description: 'Wanneer mag de actie uitgevoerd worden?',
                        required: true,
                        options: [
                          { value: 'notExecuted', label: 'Nog niet uitgevoerd (eerste keer)' },
                          { value: 'alreadyExecuted', label: 'Al eerder uitgevoerd' },
                        ],
                      },
                      expressions: {
                        hide: (field: FormlyFieldConfig) => {
                          return field.model?.uiType !== 'actions';
                        },
                        // Auto-set operator based on selection
                        'model.operator': (field: FormlyFieldConfig) => {
                          if (field.model?.actionCondition === 'notExecuted') {
                            return 'isnull';
                          } else if (field.model?.actionCondition === 'alreadyExecuted') {
                            return '!isnull';
                          }
                          return field.model?.operator;
                        },
                      },
                    },
                    {
                      key: 'filter',
                      type: 'textarea',
                      className: 'w-full',
                      props: {
                        label: 'Filter (OData)',
                        placeholder: 'entry.archived eq false and entry.user.id eq {{entry.user.id}}',
                        rows: 3,
                        description: 'Gebruik {{veldpad}} om waarden van de huidige entry te gebruiken',
                        required: false,
                      },
                      expressions: {
                        hide: (field: FormlyFieldConfig) => {
                          return field.model?.uiType !== 'externalEntries';
                        },
                      },
                    },
                    {
                      key: 'checkType',
                      type: 'select',
                      props: {
                        label: 'Loop conditie',
                        options: [
                          { value: 'noresponse', label: 'Geen entries in database' },
                          { value: 'response', label: 'Wel entries in database' },
                          { value: 'some', label: 'Sommige entries' },
                          { value: 'every', label: 'Alle entries' },
                          { value: 'none', label: 'Geen enkele entry' },
                        ],
                      },
                      expressions: {
                        hide: (field: FormlyFieldConfig) => {
                          return field.model?.uiType !== 'externalEntries';
                        },
                      },
                    },
                  ]
                : []),
              // Interface conditions are evaluated in the browser, so they get a deliberately
              // narrower external-entries shape than the action variant above: "do at least N
              // entries exist in module X matching filter Y". The action variant's `checkType`
              // (some/every/none) compares each fetched entry server-side and has no front-end
              // equivalent, so offering it here would promise behaviour we cannot deliver.
              ...(type === 'interface' && options?.allowExternalEntries
                ? [
                    {
                      key: 'moduleCode',
                      type: 'select',
                      props: {
                        label: 'Module',
                        options: this.modulesStore.modules().map(module => ({
                          value: module.code,
                          label: module.title,
                        })),
                      },
                      expressions: {
                        hide: (field: FormlyFieldConfig) => field.model?.basedOn !== 'externalEntries',
                      },
                    },
                    {
                      key: 'filter',
                      type: 'textarea',
                      className: 'w-full',
                      props: {
                        label: 'Filter (OData)',
                        placeholder: "entry.status eq 'actief'",
                        rows: 2,
                        description: 'Leeg = elk niet-gearchiveerd item van de module telt mee.',
                      },
                      expressions: {
                        hide: (field: FormlyFieldConfig) => field.model?.basedOn !== 'externalEntries',
                      },
                    },
                    {
                      key: 'minCount',
                      type: 'input',
                      defaultValue: 1,
                      props: {
                        label: 'Minimaal aantal items',
                        type: 'number',
                        min: 1,
                      },
                      expressions: {
                        hide: (field: FormlyFieldConfig) => field.model?.basedOn !== 'externalEntries',
                      },
                    },
                  ]
                : []),
              {
                key: 'compareField',
                type: 'autocomplete',
                props: {
                  label: 'Veld',
                  options: [],
                  requireSelection: false,
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) => {
                    // externalEntries asks about other entries, so there is no field on the current
                    // entry to pick.
                    return (
                      field.model?.basedOn === 'externalEntries' ||
                      field.model?.checkType === 'noresponse' ||
                      field.model?.checkType === 'response'
                    );
                  },
                  className: (field: FormlyFieldConfig) => {
                    // Disable but still send value for actions type
                    return field.model?.uiType === 'actions' ? 'hidden' : '';
                  },
                  'props.description': (field: FormlyFieldConfig) => {
                    if (field.model?.uiType === 'actions') {
                      return 'Automatisch ingesteld op basis van geselecteerde actie';
                    }
                    return '';
                  },
                  'model.compareField': (field: FormlyFieldConfig) => {
                    // Auto-set compareField for actions type
                    if (field.model?.uiType === 'actions' && field.model?.actionId) {
                      return `linkedActions.${field.model.actionId}`;
                    }
                    return field.model?.compareField;
                  },
                  'props.options': field => {
                    if (field.hide) {
                      return [];
                    }
                    // Opt-in per surface, like currentUser/externalEntries: only where the
                    // evaluator resolves it (FormService's field conditions).
                    const fieldOptions = this.#compareFieldOptions(field, module);
                    return options?.allowCurrentCompany ? [CURRENT_COMPANY_OPTION, ...fieldOptions] : fieldOptions;
                  },
                },
              },
              {
                key: 'extract',
                type: 'select',
                defaultValue: 'none',
                props: {
                  label: 'Eenheid van vergelijking',
                  options: [
                    { value: 'none', label: 'Volledige datum + tijd' },
                    { value: 'date-only', label: 'Alleen datum' },
                    { value: 'time', label: 'Alleen tijd' },
                    { value: 'month-day', label: 'Alleen maand + dag (jaaronafhankelijk)' },
                    { value: 'month', label: 'Alleen maand' },
                    { value: 'day-of-week', label: 'Alleen dag-van-week' },
                    { value: 'year', label: 'Alleen jaar' },
                    { value: 'iso-week', label: 'Alleen ISO-weeknummer' },
                    { value: 'hour-of-day', label: 'Alleen uur-van-dag' },
                  ],
                },
                expressions: {
                  hide: (field: any) => {
                    const effectiveModuleCode = this.getEffectiveModuleCode(field, module.code);
                    const target = this.getCompareField(field, effectiveModuleCode);
                    if (!target) return true;
                    return !DATE_FIELD_TYPES.includes(target.type as string);
                  },
                },
              },
              {
                key: 'compareSubField',
                type: 'select',
                defaultValue: '',
                props: {
                  label: 'Subveld',
                  options: [],
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) => {
                    // Always hide for actions type
                    if (field.model?.uiType === 'actions') {
                      return true;
                    }

                    const effectiveModuleCode = this.getEffectiveModuleCode(field, module.code);
                    const targetField = this.getCompareField(field, effectiveModuleCode);
                    return !targetField?.props?.optionsArray?.some(
                      (optionItem: OptionsItem) => optionItem.optionsType === 'module'
                    );
                  },
                  // Both expressions resolve through the same memoized call (`subFieldOptions`),
                  // so the select and its hint can never disagree, whichever expression ngx-formly
                  // happens to evaluate first.
                  'props.options': field => {
                    if (field.hide) {
                      return [];
                    }
                    return this.subFieldOptions(field, module.code, 'compare');
                  },
                  'props.description': field => {
                    if (field.hide) {
                      return undefined;
                    }
                    return this.subFieldSelectionHint(this.subFieldOptions(field, module.code, 'compare'));
                  },
                },
              },
              createFieldWithOverrides(OPERATOR_SELECT_FIELD, {
                expressions: {
                  hide: field => {
                    return (
                      field.model?.checkType === 'noresponse' ||
                      field.model?.checkType === 'response' ||
                      field.model?.uiType === 'actions'
                    );
                  },
                },
              }),
              {
                key: 'operator',
                type: 'input',
                className: 'hidden',
                props: {
                  label: 'Vergelijking',
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) => {
                    return field.model?.uiType !== 'actions';
                  },
                },
              },
              {
                template: '<div class="text-sm font-medium mt-4 mb-2">Vanaf</div>',
                expressions: {
                  hide: (field: any) => field.model?.operator !== 'between',
                },
              },
              createFieldWithOverrides(BASED_ON_SELECTED_FIELD, {
                props:
                  type === 'interface' && (options?.allowExternalEntries || options?.allowCurrentUser)
                    ? {
                        ...BASED_ON_SELECTED_FIELD.props,
                        options: [
                          ...((BASED_ON_SELECTED_FIELD.props?.options as Array<{ value: string; label: string }>) ??
                            []),
                          // Front-end evaluated by ExternalEntriesEvaluatorService. Only offered on
                          // interface conditions: the action variant above already covers the
                          // backend-evaluated shape, with loop semantics the browser cannot honour.
                          ...(options?.allowExternalEntries
                            ? [{ value: 'externalEntries', label: 'Bestaan er items in een module' }]
                            : []),
                          // Opt-in per surface (same pattern as externalEntries): only offered where
                          // the consumer's own evaluator resolves it — today that is the widget
                          // FILTERS, whose filtersToFilterString substitutes the logged-in user's
                          // id server-side. Do NOT enable on surfaces evaluated elsewhere
                          // (prominence, action conditions, validators) without teaching their
                          // evaluator this basedOn first.
                          ...(options?.allowCurrentUser ? [{ value: 'currentUser', label: 'Huidige gebruiker' }] : []),
                        ],
                      }
                    : BASED_ON_SELECTED_FIELD.props,
                expressions: {
                  hide: field => {
                    // Always hide for actions type first
                    if (field.model?.uiType === 'actions') {
                      return true;
                    }
                    // externalEntries needs the selector itself to stay reachable, so it is checked
                    // before the operator-driven rules below (it authors no operator at all).
                    if (field.model?.basedOn === 'externalEntries') {
                      return false;
                    }
                    // Hide for isnull/!isnull operators
                    if (
                      field.model?.operator === 'isnull' ||
                      field.model?.operator === '!isnull' ||
                      field.model?.operator === 'istrue' ||
                      field.model?.operator === 'isfalse'
                    ) {
                      return true;
                    }
                    // Hide for no response/response check types
                    return field.model?.checkType === 'noresponse' || field.model?.checkType === 'response';
                  },
                },
              }),
              {
                key: 'value',
                type: 'input',
                props: {
                  label: 'Waarde',
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) => {
                    if (
                      field.model?.operator === 'isnull' ||
                      field.model?.operator === '!isnull' ||
                      field.model?.operator === 'istrue' ||
                      field.model?.operator === 'isfalse' ||
                      field.model?.basedOn === 'dynamic' ||
                      field.model?.basedOn === 'currentDate' ||
                      // currentUser needs no typed value — the session supplies the id.
                      field.model?.basedOn === 'currentUser' ||
                      // externalEntries compares nothing on the current entry — the count is the
                      // whole condition.
                      field.model?.basedOn === 'externalEntries' ||
                      field.model?.checkType === 'noresponse' ||
                      field.model?.checkType === 'response'
                    ) {
                      return true;
                    }

                    // Hide for actions type (we use isnull/!isnull, no value needed)
                    if (field.model?.uiType === 'actions') {
                      return true;
                    }

                    // Also hide if we're showing a date picker instead
                    const effectiveModuleCode = this.getEffectiveModuleCode(field, module.code);
                    const targetField = this.getCompareField(field, effectiveModuleCode);
                    if (targetField) {
                      const dateTypes = DATE_FIELD_TYPES;
                      if (dateTypes.includes(targetField?.type as string)) {
                        return true; // Hide input, show date picker instead
                      }
                      if (targetField?.type === 'toggle') {
                        return true; // Hide input, show toggle instead
                      }
                    }

                    return this.hideInputIfFieldHasOptions(field, effectiveModuleCode);
                  },
                  'props.description': (field: FormlyFieldConfig) => {
                    if (field.hide) {
                      return undefined;
                    }
                    // Live, not a one-off: as long as field.model.value stays the stored array
                    // (see the onInit hook below), this keeps showing it. Once the user types, the
                    // valueChanges sync writes the typed string into the model and this goes quiet
                    // on its own.
                    return this.storedListValueHint(field.model?.value);
                  },
                },
                hooks: {
                  onInit: (field: FormlyFieldConfig) => {
                    if (field.hide) {
                      return;
                    }

                    const stored = field.model?.value;

                    // `{ emitEvent: false }` is load-bearing, not cosmetic. ngx-formly's
                    // field-form extension wires a control -> model sync subscription on
                    // `control.valueChanges` BEFORE this hook runs (FormlyField.triggerHook in
                    // node_modules/@ngx-formly/core/fesm2022/ngx-formly-core.mjs ~1998-2010 sets
                    // up `fieldChanges()` first, then invokes `hooks.onInit`). A plain
                    // `patchValue(...)` here would emit synchronously into that already-active
                    // subscription and immediately overwrite `field.model.value` with '' via
                    // `assignFieldValue` (~2145-2153 in the same file) — destroying the stored
                    // array the hint above just promised to keep.
                    //
                    // Angular's FormControl.setValue (node_modules/@angular/forms/fesm2022/
                    // forms.mjs ~2082-2091, ~1295-1311) confirms the split we rely on:
                    // `_onChange` (the view/DOM sync) always runs, but `valueChanges` — the
                    // Observable ngx-formly subscribes to — only emits `if (opts.emitEvent !==
                    // false)`. So `{ emitEvent: false }` blanks the visible control (a text input
                    // cannot render an array anyway) while leaving `field.model.value` untouched.
                    // The user's own typing still reaches the model normally: real input goes
                    // through `setValue` with the default `emitEvent: true`, which flows into the
                    // same subscription and replaces the array with what was typed.
                    field.formControl.patchValue(this.convertToString(field), { emitEvent: false });

                    if (Array.isArray(stored) && stored.length > 0) {
                      // "Laat leeg om te behouden" must stay true after editing too: typing
                      // replaces the array in the model with the typed string (normal
                      // control -> model sync), and without this, backspacing to empty would
                      // leave '' in the model — visually identical to the initial keep-state,
                      // but saving it would silently destroy the stored list. Formly's own
                      // control -> model sync subscription registered before this hook ran, so
                      // on a '' emission the model is first blanked by that sync and then
                      // restored here; `detectChanges` re-runs the expressions so the stored-
                      // value hint reappears immediately.
                      const restore = field.formControl.valueChanges.subscribe(value => {
                        if (value === '') {
                          field.model.value = stored;
                          field.options?.detectChanges?.(field);
                        }
                      });
                      // The repeat-row configs share this hook's closure, so the subscription is
                      // stashed on the field instance rather than a closure variable.
                      (field as { storedListRestore?: Subscription }).storedListRestore = restore;
                    }
                  },
                  onDestroy: (field: FormlyFieldConfig) => {
                    (field as { storedListRestore?: Subscription }).storedListRestore?.unsubscribe();
                  },
                },
              },
              // Date picker for static date comparisons
              {
                key: 'value',
                type: 'datetime',
                props: {
                  label: 'Datum',
                  required: true,
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) => {
                    // Only show for static basedOn when compareField is a date type
                    if (field.model?.basedOn !== 'static') {
                      return true;
                    }

                    const effectiveModuleCode = this.getEffectiveModuleCode(field, module.code);
                    const targetField = this.getCompareField(field, effectiveModuleCode);
                    if (!targetField) {
                      return true;
                    }

                    // Check if target field is a date type
                    const dateTypes = DATE_FIELD_TYPES;
                    return !dateTypes.includes(targetField?.type as string);
                  },
                },
              },
              // toggle for static toggle comparisons
              {
                key: 'value',
                type: 'toggle',
                props: {
                  label: 'Waarde',
                  required: false,
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) => {
                    // Only show for static basedOn when compareField is a toggle type
                    if (field.model?.basedOn !== 'static') {
                      return true;
                    }

                    const effectiveModuleCode = this.getEffectiveModuleCode(field, module.code);
                    const targetField = this.getCompareField(field, effectiveModuleCode);
                    if (!targetField) {
                      return true;
                    }

                    return targetField?.type !== 'toggle';
                  },
                },
              },
              {
                key: 'value',
                type: 'custom-select',
                props: {
                  multiple: true,
                  label: 'Waarde',
                  expressionsOptions: true,
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) => {
                    if (
                      field.model?.operator === 'isnull' ||
                      field.model?.operator === '!isnull' ||
                      field.model?.operator === 'istrue' ||
                      field.model?.operator === 'isfalse' ||
                      field.model?.basedOn === 'dynamic' ||
                      field.model?.basedOn === 'currentDate' ||
                      field.model?.checkType === 'noresponse' ||
                      field.model?.checkType === 'response'
                    ) {
                      return true;
                    }

                    // Hide select field for actions type (we use input field instead)
                    if (field.model?.uiType === 'actions') {
                      return true;
                    }

                    // Also hide if we're showing a date picker instead
                    const effectiveModuleCode = this.getEffectiveModuleCode(field, module.code);
                    const targetField = this.getCompareField(field, effectiveModuleCode);
                    if (targetField) {
                      const dateTypes = DATE_FIELD_TYPES;
                      if (dateTypes.includes(targetField?.type as string)) {
                        return true; // Hide select, show date picker instead
                      }
                    }

                    return !this.hideInputIfFieldHasOptions(field, effectiveModuleCode);

                    // const moduleCode = this.formService.findFieldValue(field, 'moduleCode');
                    //
                    // return !this.compareFieldHasOptions(field, moduleCode);
                  },
                  'props.options': (field: FormlyFieldConfig) => {
                    if (field.hide) {
                      return [];
                    }

                    // const moduleCode = this.formService.findFieldValue(field, 'moduleCode');
                    // return this.getCompareFieldOptions(field, moduleCode);

                    const effectiveModuleCode = this.getEffectiveModuleCode(field, module.code);
                    let targetSubField;
                    const targetField = this.getCompareField(field, effectiveModuleCode);

                    if (targetField.type === 'custom-userroles-multicheckbox') {
                      return this.userRolesStore.userRoles().map(role => ({
                        label: role.name,
                        value: role.id,
                      }));
                    }

                    const optionItem = targetField?.props?.optionsArray?.find(
                      (optionItem: OptionsItem) => optionItem.optionsType === 'module'
                    );
                    if (optionItem) {
                      targetSubField = this.getCompareSubField(field, optionItem.moduleCode);
                    }

                    if (targetSubField) {
                      return this.getOptionsOfTargetField(targetSubField);
                    }
                    return this.getOptionsOfTargetField(targetField);
                  },
                },
                hooks: {
                  onChanges: (field: FormlyFieldConfig) => {
                    if (field.hide) {
                      return;
                    }

                    field.formControl.patchValue(this.checkMultiSelectArray(field));
                  },
                },
              },
              {
                key: 'value',
                type: 'autocomplete',
                props: {
                  label: 'Waarde',
                  expressionsOptions: true,
                  requireSelection: false,
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) => {
                    if (
                      field.model?.operator === 'isnull' ||
                      field.model?.operator === '!isnull' ||
                      field.model?.operator === 'istrue' ||
                      field.model?.operator === 'isfalse' ||
                      field.model?.basedOn === 'static' ||
                      field.model?.basedOn === 'currentDate' ||
                      field.model?.checkType === 'noresponse' ||
                      field.model?.checkType === 'response'
                    ) {
                      return true;
                    }

                    // Hide dynamic field for actions type (not applicable)
                    if (field.model?.uiType === 'actions') {
                      return true;
                    }
                  },
                  'props.options': (field: FormlyFieldConfig) => {
                    if (field.hide) {
                      return [];
                    }
                    const excludeTypes: string[] = ['pages', 'tabs', 'list', 'linked-items', 'required-items'];

                    const options = this.formService.getFieldsOptionsOfModule(
                      this.modulesStore.generatedForms()[module.id],
                      excludeTypes,
                      true,
                      this.modulesStore.generatedForms(),
                      this.modulesStore.moduleCodeMapper()
                    );
                    options.unshift({ label: 'Geen', value: '', type: '' });
                    return options;
                  },
                },
                hooks: {
                  onChanges: (field: FormlyFieldConfig) => {
                    if (field.hide) {
                      return;
                    }
                  },
                },
              },
              {
                key: 'datatype',
                type: 'input',
                className: 'hidden',
                props: {
                  label: 'Datatype',
                },
                expressions: {
                  'model.datatype': (field: FormlyFieldConfig) => {
                    // get the datatype of fieldkeyPath

                    // `moduleCode` only exists on an externalEntries row; every other row —
                    // widget filters included — takes the module this condition set was built
                    // for. Without that fallback the datatype stayed empty, and FiltersService
                    // then compared array fields with `eq`, which matches nothing in Cosmos.
                    const effectiveModuleCode =
                      this.formService.findFieldValue(field, 'moduleCode') ||
                      this.getEffectiveModuleCode(field, module.code);
                    const selectedModule = this.modulesStore
                      .modules()
                      .find(module => module.code === effectiveModuleCode);

                    if (selectedModule === undefined) {
                      return;
                    }

                    const fieldsOfTargetModule = FormHelper.getFields(
                      this.modulesStore.generatedForms()[selectedModule.id]
                    );

                    const targetField = fieldsOfTargetModule.find(fieldOfTargetModule => {
                      return (
                        fieldOfTargetModule.props.keyPath != undefined &&
                        fieldOfTargetModule.props.keyPath === field.model?.compareField
                      );
                    });
                    if (targetField === undefined) {
                      return;
                    }

                    return targetField.props.datatype;
                  },
                },
              },
              {
                key: 'valueSubField',
                type: 'select',
                defaultValue: '',
                props: {
                  label: 'Subveld van waarde',
                  options: [],
                },
                expressions: {
                  hide: (field: FormlyFieldConfig) => {
                    const targetField = this.getFieldKeyPathField(field, module.code);
                    return !targetField?.props?.optionsArray?.some(
                      (optionItem: OptionsItem) => optionItem.optionsType === 'module'
                    );
                  },
                  // Same memoized-resolver pattern as compareSubField above — evaluation order
                  // between the two expressions is irrelevant.
                  'props.options': field => {
                    if (field.hide) {
                      return [];
                    }
                    return this.subFieldOptions(field, module.code, 'value');
                  },
                  'props.description': field => {
                    if (field.hide) {
                      return undefined;
                    }
                    return this.subFieldSelectionHint(this.subFieldOptions(field, module.code, 'value'));
                  },
                },
              },
              // Consolidated useOffset toggle for all date scenarios
              {
                key: 'useOffset',
                type: 'checkbox',
                props: {
                  label: 'Relatieve datum',
                  description: 'Verschuif de datum automatisch',
                },
                defaultValue: false,
                expressions: {
                  hide: (field: FormlyFieldConfig) => {
                    const effectiveModuleCode = this.getEffectiveModuleCode(field, module.code);
                    // For static dates, check if compareField is a date type
                    if (field.model?.basedOn === 'static') {
                      const targetField = this.getCompareField(field, effectiveModuleCode);
                      if (!targetField) {
                        return true;
                      }
                      const dateTypes = DATE_FIELD_TYPES;
                      return !dateTypes.includes(targetField?.type as string);
                    }

                    // For dynamic values, check if selected field is a date type
                    if (field.model?.basedOn === 'dynamic') {
                      const dynamicFieldPath = field.model?.value;
                      if (!dynamicFieldPath) {
                        return true;
                      }
                      const selectedModule = this.modulesStore.modules().find(m => m.code === effectiveModuleCode);
                      if (!selectedModule) {
                        return true;
                      }
                      const targetField = this.formService
                        .getFieldsOptionsOfModule(this.modulesStore.generatedForms()[selectedModule.id])
                        .find(field => field.value === dynamicFieldPath);
                      if (!targetField) {
                        return true;
                      }
                      const dateTypes = DATE_FIELD_TYPES;
                      return !dateTypes.includes(targetField.type as string);
                    }

                    // For currentDate, always show
                    if (field.model?.basedOn === 'currentDate') {
                      return false;
                    }

                    // Hide in all other cases
                    return true;
                  },
                },
              },
              // Shared date offset controls for all basedOn types
              {
                fieldGroup: CONDITION_DATE_OFFSET_FIELDS,
                props: {
                  label: 'Datum verschuiving',
                },
                className: 'ml-2 rounded-r-lg',
                expressions: {
                  hide: (field: FormlyFieldConfig) => {
                    // Hide if none of the useOffset toggles are enabled
                    if (!field.model?.useOffset) {
                      return true;
                    }

                    const effectiveModuleCode = this.getEffectiveModuleCode(field, module.code);
                    // For static dates, check if compareField is a date type
                    if (field.model?.basedOn === 'static') {
                      const targetField = this.getCompareField(field, effectiveModuleCode);
                      if (!targetField) {
                        return true;
                      }
                      const dateTypes = DATE_FIELD_TYPES;
                      return !dateTypes.includes(targetField?.type as string);
                    }

                    // For dynamic values, check if selected field is a date type
                    if (field.model?.basedOn === 'dynamic') {
                      const dynamicFieldPath = field.model?.value;
                      if (!dynamicFieldPath) {
                        return true;
                      }
                      const selectedModule = this.modulesStore.modules().find(m => m.code === effectiveModuleCode);
                      if (!selectedModule) {
                        return true;
                      }
                      const targetField = this.formService
                        .getFieldsOptionsOfModule(this.modulesStore.generatedForms()[selectedModule.id])
                        .find(field => field.value === dynamicFieldPath);
                      if (!targetField) {
                        return true;
                      }
                      const dateTypes = DATE_FIELD_TYPES;
                      return !dateTypes.includes(targetField.type as string);
                    }

                    // For currentDate, just check if basedOn is currentDate
                    if (field.model?.basedOn === 'currentDate') {
                      return false;
                    }

                    return true;
                  },
                  'props.description': (field: FormlyFieldConfig) => {
                    const amount = field.model?.dateOffset?.dateOffsetValue;
                    const unit = field.model?.dateOffset?.dateOffsetUnit;
                    const direction = field.model?.dateOffset?.dateOffsetType;
                    if (amount != null && unit && direction) {
                      const unitLabel = UNIT_LABELS[unit as keyof typeof UNIT_LABELS] || unit;
                      const directionLabel = direction === 'before' ? 'ervoor' : 'erna';
                      const suffix = field.model?.basedOn === 'currentDate' ? ' dan nu' : '';
                      return `Resultaat: ${amount} ${unitLabel} ${directionLabel}${suffix}`;
                    }
                    return 'Configureer de verschuiving hierboven';
                  },
                },
              },
              {
                template: '<div class="text-sm font-medium mt-4 mb-2">Tot</div>',
                expressions: {
                  hide: (field: any) => field.model?.operator !== 'between',
                },
              },
              {
                key: 'basedOn2',
                type: 'select',
                defaultValue: 'static',
                props: {
                  label: 'Beoordeel op',
                  options: [
                    { value: 'static', label: 'Vaste waarde' },
                    { value: 'dynamic', label: 'Waarde van invoerveld' },
                    { value: 'currentDate', label: 'Waarde van huidige tijd en datum' },
                  ],
                },
                expressions: {
                  hide: (field: any) => field.model?.operator !== 'between',
                },
              },
              {
                key: 'value2',
                type: 'input',
                props: { label: 'Waarde' },
                expressions: {
                  hide: (field: any) => field.model?.operator !== 'between' || field.model?.basedOn2 !== 'static',
                },
              },
              {
                key: 'useOffset2',
                type: 'checkbox',
                defaultValue: false,
                props: { label: 'Met offset' },
                expressions: {
                  hide: (field: any) => field.model?.operator !== 'between' || field.model?.basedOn2 !== 'currentDate',
                },
              },
              // Reuse CONDITION_DATE_OFFSET_FIELDS: clone with key-prefix swap dateOffset.* → dateOffset2.*
              ...CONDITION_DATE_OFFSET_FIELDS.map(f => ({
                ...f,
                key: (f.key as string).replace(/^dateOffset\./, 'dateOffset2.'),
                expressions: {
                  hide: (field: any) =>
                    field.model?.operator !== 'between' ||
                    field.model?.basedOn2 !== 'currentDate' ||
                    field.model?.useOffset2 !== true,
                },
              })),
            ],
          },
        ],
      },
    };
    return basicConditionsField;
  }
}
